import { useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { useConditionalEffect } from 'react-delta';
import { useQuery } from '@apollo/client';

import ELEMENT from 'common/element';
import { AccessType } from 'common/graphqlTypes';
import { GROUPS_LIST } from 'components/manageGroups/queries';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import PageHeader from 'components/common/PageHeader/PageHeader';
import Page from 'components/common/Page/Page';
import GroupsTable from 'components/manageGroups/components/GroupsTable';
import EditGroupDialog from 'components/manageGroups/components/EditGroupDialog';
import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';

export type Group = {
  allowedDashboards?: Array<string>;
  allowedSearch?: string;
  displayName: string;
  id: string;
  permission: AccessType;
  totalMembers: number;
};

/**
 * Page that allows user with admin rights to manage access/restrictions per SCIM group
 */
const ManageGroupsPage = () => {
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const { loading, data, error } = useQuery(GROUPS_LIST);
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);

  useConditionalEffect(() => {
    setPageAlertConfig({ message: 'Unable to load groups.', variant: PageAlertVariant.ERROR, autoHide: true });
  }, !!error);

  const handleEditClick = (group: Group): void => {
    setSelectedGroup(group);
  };

  const groupsSection = (
    <>
      {data?.groups ? <GroupsTable groups={data.groups} handleEdit={handleEditClick} /> : null}
      {loading ? <LoadingPlaceholder /> : null}
    </>
  );

  return (
    <Page docTitle="Groups" testId={ELEMENT.manageGroupsPage}>
      <div className="ManageGroupsPage">
        <PageHeader
          title="Manage Groups"
          helpLink="help/users" // TODO: I don't think we have dedicated documentation for SCIM groups.
        />
        <div className="ManageGroupsPage__Header">
          <p>Manage groups created in a third-party identity provider</p>
        </div>
        {groupsSection}
        <EditGroupDialog show={!!selectedGroup} group={selectedGroup} close={(): void => setSelectedGroup(null)} />
      </div>
    </Page>
  );
};

export default ManageGroupsPage;
